import React from 'react';
import type {WeatherWarning} from '../api/types';
import * as Dates from '../utils/Dates';
import {currentWarnings} from '../utils/WeatherWarnings';

/**
 * Longest single wait for the next boundary. A timer set days ahead is at the
 * mercy of a suspended JS thread and a changed device clock; re-checking every
 * hour costs nothing and bounds how stale the list can get.
 */
const MAX_WAIT_MS = 60 * 60 * 1000;

/**
 * The warnings that have not ended yet (`WeatherWarnings.currentWarnings`: a
 * null `end_time` never ends), kept current while the screen stays mounted.
 *
 * The backend caches the point response for up to an hour and the store keeps
 * it until the next refresh, so a warning that ends in the meantime is still in
 * the data. Filtering once on render would leave it on screen until something
 * else re-rendered; instead one timer is armed for the next boundary among the
 * current warnings (an end, or a start: an upcoming warning turning active
 * changes what an "active" count or section shows), and firing it re-reads the
 * clock and re-renders.
 */
const useCurrentWarnings = (
  warnings: readonly WeatherWarning[],
): WeatherWarning[] => {
  // Bumped by the timer. The clock itself is re-read on every recomputation,
  // so warnings that arrive long after mount are filtered against the time
  // they arrived, not the time of the last tick.
  const [clock, setClock] = React.useState(Dates.now);

  const current = React.useMemo(
    () => currentWarnings(warnings, Math.max(clock, Dates.now())),
    [warnings, clock],
  );

  React.useEffect(() => {
    const now = Dates.now();
    const boundaries = current
      .flatMap((w) => [w.start_time, w.end_time])
      .filter(
        (t): t is number =>
          typeof t === 'number' && Number.isFinite(t) && t > now,
      );
    const next = Math.min(...boundaries);
    if (!Number.isFinite(next)) {
      return undefined;
    }
    const wait = Math.min((next - now) * 1000, MAX_WAIT_MS);
    const timeout = setTimeout(() => setClock(Dates.now()), wait);
    return () => clearTimeout(timeout);
  }, [current]);

  return current;
};

export default useCurrentWarnings;
